import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CallTrackerProvider } from "@/components/CallTracker";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import { loginPath } from "@/lib/api";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Quotes from "@/pages/Quotes";
import Proposals from "@/pages/Proposals";
import Policies from "@/pages/Policies";
import Renewals from "@/pages/Renewals";
import Payments from "@/pages/Payments";
import Commissions from "@/pages/Commissions";
import Expenses from "@/pages/Expenses";
import Insurers from "@/pages/Insurers";
import Claims from "@/pages/Claims";
import Tickets from "@/pages/Tickets";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import LoanFollowups from "@/pages/LoanFollowups";
import LoanDashboard from "@/pages/LoanDashboard";
import LoanLeads from "@/pages/LoanLeads";
import LoanApplications from "@/pages/LoanApplications";
import LoanCommissions from "@/pages/LoanCommissions";
import Lenders from "@/pages/Lenders";
import LoanProducts from "@/pages/LoanProducts";
import LeadImport from "@/pages/LeadImport";
import Agents from "@/pages/Agents";
import AgentDetail from "@/pages/AgentDetail";
import AgentPayouts from "@/pages/AgentPayouts";
import PublicLoanApply from "@/pages/PublicLoanApply";
import AssignmentRules from "@/pages/AssignmentRules";
import Integrations from "@/pages/Integrations";
import Workspace from "@/pages/Workspace";
import FormBuilder from "@/pages/FormBuilder";
import LeadScoring from "@/pages/LeadScoring";
import CampaignCosts from "@/pages/CampaignCosts";
import TeamPerformance from "@/pages/TeamPerformance";
import Security from "@/pages/Security";
import PublicStatus from "@/pages/PublicStatus";
import EmployeeSales from "@/pages/EmployeeSales";
import Website from "@/pages/Website";
import { ForgotPassword, ResetPassword } from "@/pages/ForgotPassword";
import { ForcePasswordChange } from "@/components/PasswordForms";

function HomeGate() {
  const { user, ready } = useAuth();
  if (ready && user === false) return <Website />;
  return <Protected><Home /></Protected>;
}

function Protected({ children, perm }) {
  const { user, ready, hasPerm, perms, completeLogin, logout } = useAuth();
  if (!ready || user === null) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (user === false) return <Navigate to={loginPath()} replace />;
  if (user.must_change_password) {
    return <ForcePasswordChange user={user} onLogout={logout} onDone={() => completeLogin({ user: { ...user, must_change_password: false }, permissions: perms })} />;
  }
  if (perm && !hasPerm(perm)) {
    return <Layout><div className="max-w-md mx-auto mt-24 text-center rounded-xl border border-border bg-card p-8" data-testid="not-authorized">
      <h2 className="text-lg font-bold">You don't have access to this page</h2>
      <p className="text-sm text-muted-foreground mt-2">Ask your admin if you need this permission.</p>
    </div></Layout>;
  }
  return <Layout>{children}</Layout>;
}

function Home() {
  const { user, hasPerm } = useAuth();
  const dept = hasPerm("*") ? "both" : (user?.department || "both");
  if (dept === "loans") return <Navigate to="/loans" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider><CallTrackerProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/super-admin-login" element={<Login portal="admin" />} />
            <Route path="/apply-loan" element={<PublicLoanApply />} />
            <Route path="/status" element={<PublicStatus />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/area-routing" element={<Protected perm="leads:assign"><AssignmentRules /></Protected>} />
            <Route path="/integrations" element={<Protected perm="integrations:manage"><Integrations /></Protected>} />
            <Route path="/workspace" element={<Protected><Workspace /></Protected>} />
            <Route path="/loan-forms" element={<Protected perm="form_builder:manage"><FormBuilder /></Protected>} />
            <Route path="/lead-scoring" element={<Protected perm="leads:view"><LeadScoring /></Protected>} />
            <Route path="/security" element={<Protected><Security /></Protected>} />
            <Route path="/campaign-costs" element={<Protected perm="campaign_costs:view"><CampaignCosts /></Protected>} />
            <Route path="/team-performance" element={<Protected perm="leads:assign"><TeamPerformance /></Protected>} />
            <Route path="/" element={<HomeGate />} />
            <Route path="/leads" element={<Protected><Leads /></Protected>} />
            <Route path="/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
            <Route path="/quotes" element={<Protected><Quotes /></Protected>} />
            <Route path="/proposals" element={<Protected><Proposals /></Protected>} />
            <Route path="/policies" element={<Protected><Policies /></Protected>} />
            <Route path="/renewals" element={<Protected perm="renewals:view"><Renewals /></Protected>} />
            <Route path="/claims" element={<Protected><Claims /></Protected>} />
            <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
            <Route path="/payments" element={<Protected><Payments /></Protected>} />
            <Route path="/commissions" element={<Protected><Commissions /></Protected>} />
            <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
            <Route path="/insurers" element={<Protected><Insurers /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/lead-import" element={<Protected><LeadImport /></Protected>} />
            <Route path="/employee-sales" element={<Protected><EmployeeSales /></Protected>} />
            <Route path="/users" element={<Protected perm="users:password"><Users /></Protected>} />
            <Route path="/audit" element={<Protected><Audit /></Protected>} />
            <Route path="/loans" element={<Protected><LoanDashboard /></Protected>} />
            <Route path="/loan-leads" element={<Protected><LoanLeads /></Protected>} />
            <Route path="/loan-applications" element={<Protected><LoanApplications /></Protected>} />
            <Route path="/loan-commissions" element={<Protected><LoanCommissions /></Protected>} />
            <Route path="/lenders" element={<Protected><Lenders /></Protected>} />
            <Route path="/loan-products" element={<Protected><LoanProducts /></Protected>} />
            <Route path="/loan-followups" element={<Protected><LoanFollowups /></Protected>} />
            <Route path="/agents" element={<Protected perm="agents:view"><Agents /></Protected>} />
            <Route path="/agents/:id" element={<Protected perm="agents:view"><AgentDetail /></Protected>} />
            <Route path="/agent-payouts" element={<Protected perm="agents:payout"><AgentPayouts /></Protected>} />
            <Route path="/settings" element={<Protected perm="*"><Settings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </CallTrackerProvider></AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
